import React, { Component } from 'react';
import PropTypes from 'prop-types';

import {
  UTILITY_USER_ROLE_ADMIN,
  UTILITY_USER_ROLE_PRESERVE_ON_DEV,
} from 'common/utility/userRoles';

import { AUTOMATED_ORDER_PLACER_TYPE_ATT } from 'common/utility/constants';

import {
  SR_API_ACTION_ADDRESS_FCC_SCRAPE,
  SR_API_ACTION_ADDRESS_FCC_SCRAPE_ALL,
  SR_API_ACTION_LISTINGS_SCRAPER_SCRAPE_URL,
  SR_API_ACTION_UTILITY_ACTIVATION_MISSING_PROVIDERS_AUTO_MAP,
  SR_API_ACTION_UTILITY_PARTNER_ADDRESS_AUTO_MAP_PROVIDERS,
  SR_API_ACTION_UTILITY_ACTIVATION_SERVICE_AREAS_AUTO_MAP,
  SR_API_ACTION_UTILITY_ALGOLIA_RESET_INDEX,
  SR_API_ACTION_LISTINGS_SCRAPER_SCRAPE_BY_PARTNER_SLUG,
  SR_API_ACTION_UTILITY_USER_GET_FROM_API_TEST,
} from 'api/utilityActionTypes';

import { DATA_TABLE_SOURCE_UTILITY_PARTNER_PMS_INTEGRATIONS } from 'common/dataTableConstants';

import branch from 'client/core/branch';
import withScreenId from 'client/helpers/withScreenId';
import withTransitionHook from 'client/helpers/withTransitionHook';
import allowedUtilityRoles from 'client/helpers/allowedUtilityRoles';
import withScrollToTop from 'client/helpers/withScrollToTop';
import { SCREEN_ID_UTILITY_PROFIT_ADMIN } from 'client/constants/screenIds';

import requestUtilityApi from 'client/actions/requestUtilityApi';
import AdminActions from 'client/actions/Admin';
import UtilityAuthActions from 'client/actions/UtilityAuth';
import UtilityPartnerActions from 'client/actions/UtilityPartner';
import UtilityActivationActions from 'client/actions/UtilityActivation';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';
import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrPartnerSection from 'client/components/SrPartnerSection';
import SrButton, { SR_BUTTON_THEME_ADOBE_YELLOW } from 'client/components/SrButton/SrButton';
import SrLinkUrlButton from 'client/components/SrLinkUrlButton';
import UPDataTablePartners from 'client/components/UPDataTablePartners/UPDataTablePartners';
import UPLoginAsForm from 'client/components/UPLoginAsForm/UPLoginAsForm';
import SrUtilityProfitAddressesDataTable from 'client/components/SrUtilityProfitAddressesDataTable/SrUtilityProfitAddressesDataTable';
import UPBulkImportTenantForm from 'client/components/UPBulkImportTenantForm/UPBulkImportTenantForm';
import UPButtonCronJobMissingInternetPlanVariants from 'client/components/UPButtonMissingInternetPlanVariants';
import UPIngestServiceAreaForm from 'client/components/UPIngestServiceAreaForm/UPIngestServiceAreaForm';
import UPPartnerSyncPMSoftwareForm from 'client/components/UPPartnerSyncPMSoftwareForm/UPPartnerSyncPMSoftwareForm';
import UPDataTableListingScraperLogGroupIndex from 'client/components/UPDataTableListingScraperLogGroupIndex/UPDataTableListingScraperLogGroupIndex';
import SrUtilityProfitActivationsDataTable from 'client/components/SrUtilityProfitActivationsDataTable/SrUtilityProfitActivationsDataTable';
import UPOrderPlacingForm from 'client/components/UPOrderPlacingForm/UPOrderPlacingForm';
import UPFileImportAdminModal from 'client/components/UPFileImportAdminModal/UPFileImportAdminModal';
import SrDataTableConnected from 'client/components/SrDataTableConnected';

@withTransitionHook
@withScreenId(SCREEN_ID_UTILITY_PROFIT_ADMIN)
@withScrollToTop
@allowedUtilityRoles({
  roles: [UTILITY_USER_ROLE_ADMIN],
})
@branch({
  utilityUser: ['utilityUser'],
  utilityTypes: ['utilityActivations', 'types'],
  utilityPartnerList: ['utilityPartners', 'list'],
})
class UtilityProfitAdminPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Admin | Utility Profit',
  });

  static fetchData(match, inject) {
    return Promise.all([
      UtilityPartnerActions.getList(inject),
      UtilityActivationActions.getTypesList(inject),
    ]);
  }

  constructor(props) {
    super(props);
    this.state = {
      scrapedListings: null,
      scrapedListingFeed: null,
      showAttOrderModal: false,
      showImportFileModal: false,
    };
  }

  _runCronTaskOnDemand = () => {
    const cronTaskName = global.prompt('Cron task name:');

    if (
      !cronTaskName ||
      !global.confirm(`Are you sure you want to run the cron task "${cronTaskName}"?`)
    ) {
      return;
    }
    this.props.dispatch(AdminActions.runCronTask, { cronTaskName });
  };

  _runCronTask = (cronTaskName) => {
    if (!global.confirm(`Are you sure you want to run the cron task "${cronTaskName}"?`)) {
      return;
    }
    this.props.dispatch(AdminActions.runCronTask, { cronTaskName });
  };

  _runFccScraper = () => {
    const { dispatch } = this.props;

    const addressIdStr = global.prompt('Address ID:');
    const addressId = parseInt(addressIdStr, 10);

    if (addressId) {
      dispatch(requestUtilityApi, {
        action: SR_API_ACTION_ADDRESS_FCC_SCRAPE,
        payload: {
          addressId,
        },
        onSuccess() {
          global.alert('FCC scraper started.');
        },
        onError(error) {
          global.alert(`Error while starting the FCC scraper: ${error.message}`);
        },
      });
    } else if (addressIdStr) {
      global.alert(`Invalid address ID: ${addressIdStr}`);
    }
  };

  _runFccScraperAll = () => {
    const { dispatch } = this.props;
    if (
      !global.confirm('Are you sure you want to re-run the scraper on ALL the unmapped addresses?')
    ) {
      return;
    }

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_ADDRESS_FCC_SCRAPE_ALL,
      onSuccess() {
        global.alert('Started scraping ALL unmapped address.');
      },
      onError(error) {
        global.alert(`Error while starting to scrape all addresses: ${error.message}`);
      },
    });
  };

  _autoMapProvidersByConfigurations = () => {
    const { dispatch } = this.props;
    if (!global.confirm('Are you sure you want to auto map providers by configurations?')) {
      return;
    }
    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_ACTIVATION_MISSING_PROVIDERS_AUTO_MAP,
      onSuccess() {
        global.alert('Started auto-mapping missing provider activations.');
      },
      onError(error) {
        global.alert(`Error while starting the auto-mapping: ${error.message}`);
      },
    });
  };

  _autoMapProvidersByServiceAreas = () => {
    const { dispatch } = this.props;
    if (!global.confirm('Are you sure you want to auto map providers by service areas?')) {
      return;
    }
    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_ACTIVATION_SERVICE_AREAS_AUTO_MAP,
      onSuccess() {
        global.alert('Started auto-mapping by service area.');
      },
      onError(error) {
        global.alert(`Error while starting the auto-mapping: ${error.message}`);
      },
    });
  };

  _autoMapAddress = () => {
    const { dispatch } = this.props;
    let addressId = global.prompt('Address ID:');
    addressId = parseInt(addressId, 10);

    let utilityPartnerId = global.prompt('Utility Partner ID:');
    utilityPartnerId = parseInt(utilityPartnerId, 10);

    if (!addressId || !utilityPartnerId) {
      global.alert('Invalid address ID or utility partner ID.');
      return;
    }

    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PARTNER_ADDRESS_AUTO_MAP_PROVIDERS,
      payload: {
        addressId,
        utilityPartnerId,
      },
      onSuccess() {
        global.alert('Started auto-mapping address.');
      },
      onError(error) {
        global.alert(`Error while starting the auto-mapping of address: ${error.message}`);
      },
    });
  };

  _reindexAlgoliaIndex = () => {
    const { dispatch } = this.props;
    if (!global.confirm('Are you sure you want to re-index the Algolia search index?')) {
      return;
    }

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_ALGOLIA_RESET_INDEX,
      onSuccess() {
        global.alert('Ok.');
      },
      onError(error) {
        global.alert(`Error while re-creating the Algolia index: ${error.message}`);
      },
    });
  };

  _testUtilityApi = () => {
    const { dispatch } = this.props;
    const userIdStr = global.prompt('User ID to load:');
    const userId = parseInt(userIdStr || '', 10);
    if (!userId) {
      return;
    }
    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_USER_GET_FROM_API_TEST,
      payload: {
        userId,
      },
      onSuccess(response) {
        global.alert(JSON.stringify(response, null, 2));
      },
      onError(error) {
        global.alert(`Error while calling the UtilityProfit API: ${error.message}`);
      },
    });
  };

  _onLogonasFormSubmit = (formValues) => {
    const { dispatch } = this.props;
    dispatch(UtilityAuthActions.logonas, {
      phone: formValues.phone,
      redirectUrl: global.location.origin,
    });
  };

  _testListingsScraperUrl = () => {
    const { dispatch } = this.props;
    const url = global.prompt('Listings URL:');

    if (!url) {
      return;
    }

    this.setState({
      scrapedListings: `Scraping ${url}, please wait...`,
    });

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_LISTINGS_SCRAPER_SCRAPE_URL,
      payload: {
        url,
      },
      onSuccess: (result) => {
        this.setState({ scrapedListings: result });
      },
      onError: (error) => {
        global.alert(`Error while running the listings scraper: ${error.message}`);
      },
    });
  };

  _testListingsScraperFeed = () => {
    const { dispatch } = this.props;
    const partnerSlug = global.prompt('Partner slug:');

    if (!partnerSlug) {
      return;
    }

    this.setState({
      scrapedListingFeed: `Scraping partner: ${partnerSlug}, please wait...`,
    });

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_LISTINGS_SCRAPER_SCRAPE_BY_PARTNER_SLUG,
      payload: {
        partnerSlug,
      },
      onSuccess: (result) => {
        this.setState({ scrapedListingFeed: result });
      },
      onError: (error) => {
        global.alert(`Error while running the listings scraper: ${error.message}`);
      },
    });
  };

  _toggleAttOrderModal = () => {
    this.setState((prevState) => ({ showAttOrderModal: !prevState.showAttOrderModal }));
  };

  _toggleImportFileModal = () => {
    this.setState((prevState) => ({ showImportFileModal: !prevState.showImportFileModal }));
  };

  render() {
    const { utilityUser, utilityTypes, utilityPartnerList } = this.props;

    const { scrapedListings, scrapedListingFeed } = this.state;

    return (
      <SrPartnerPageV2
        navbarTitle="Utility Profit Admin"
        navbarImage="/images/utility-profit-logo.svg"
        backUrl="/admin"
        redirectToLastLocation={false}
        maxWidth="1200px"
      >
        <SrLinkUrlButton
          buttonText="Copy Partner Signup Link"
          className="utilityActivationLink"
          hideUrl
          href={`https://utilityprofit.com/signup/${utilityUser?.id}`}
        />

        <UPDataTablePartners />

        <SrBox padding="30px 0 0">
          <SrUtilityProfitActivationsDataTable
            showPartner
            utilityTypes={utilityTypes || []}
            utilityUser={utilityUser}
          />
        </SrBox>

        <SrBox padding="30px 0 0">
          <SrUtilityProfitAddressesDataTable
            title="Addresses"
            showPartner
            utilityTypes={utilityTypes || []}
            utilityPartnerList={utilityPartnerList}
          />
        </SrBox>

        <SrPartnerSection title="Login As" padding="30px 0 0" titleWeight="700">
          <UPLoginAsForm />
        </SrPartnerSection>

        <SrPartnerSection title="Accounting" padding="30px 0 0" titleWeight="700">
          <SrBox>
            <SrLink to="/utility-profit/admin/dashboard/accounting">
              <SrText font="greycliff" size="16" weight="700" lineHeight="50px" color="blue300">
                Internet Accounting Dashboard
              </SrText>
            </SrLink>
          </SrBox>
          <SrBox>
            <SrLink to="/utility-profit/admin/dashboard/accounting-non-internet">
              <SrText font="greycliff" size="16" weight="700" lineHeight="50px" color="blue300">
                Non-Internet Accounting Dashboard
              </SrText>
            </SrLink>
          </SrBox>
        </SrPartnerSection>

        <SrPartnerSection title="Mapping" padding="30px 0 0" titleWeight="700">
          <SrBox>
            <SrLink to="/utility-profit/admin/dashboard/mapping-hitlist">
              <SrText font="greycliff" size="16" weight="700" lineHeight="50px" color="blue300">
                Mapping Dashboard
              </SrText>
            </SrLink>
          </SrBox>
          <SrBox>
            <SrLink to="/utility-profit/admin/dashboard/pms-processing-tool">
              <SrText font="greycliff" size="16" weight="700" lineHeight="50px" color="blue300">
                Map Pending Gmail Attachments to Partners
              </SrText>
            </SrLink>
          </SrBox>
        </SrPartnerSection>

        <SrPartnerSection title="Orders" padding="30px 0 0" titleWeight="700">
          <SrBox>
            <SrLink to="/utility-profit/admin/dashboard/order-hitlist/non-delayed-orders">
              <SrText font="greycliff" size="16" weight="700" lineHeight="50px" color="blue300">
                Dashboard
              </SrText>
            </SrLink>
          </SrBox>
          <SrLink to="/utility-profit/admin/automatic-orders">
            <SrText font="greycliff" size="16" weight="700" lineHeight="50px" color="blue300">
              Automatic Orders
            </SrText>
          </SrLink>
        </SrPartnerSection>

        <SrPartnerSection title="BCC Reminder" padding="30px 0 0" titleWeight="700">
          <SrLink to="/utility-profit/admin/bcc-reminders">
            <SrText font="greycliff" size="16" weight="700" lineHeight="50px" color="blue300">
              Send BCC Reminder
            </SrText>
          </SrLink>
        </SrPartnerSection>

        <SrPartnerSection title="Providers" padding="30px 0 0" titleWeight="700">
          <SrLink to="/utility-profit/admin/providers">
            <SrText font="greycliff" size="16" weight="700" lineHeight="50px" color="blue300">
              Manage Providers
            </SrText>
          </SrLink>
        </SrPartnerSection>

        <SrPartnerSection title="Actions" padding="30px 0 0" titleWeight="700">
          {utilityUser.roles.includes(UTILITY_USER_ROLE_PRESERVE_ON_DEV) && (
            <SrButton
              type="button"
              onClick={() => this._runCronTask('cronScrapeListingFeeds')}
              theme={SR_BUTTON_THEME_ADOBE_YELLOW}
            >
              Scrape Listing Feeds
            </SrButton>
          )}
          <SrButton
            type="button"
            onClick={this._runCronTaskOnDemand}
            theme={SR_BUTTON_THEME_ADOBE_YELLOW}
            margin="20px 0"
          >
            Run Cron Task
          </SrButton>
          <SrButton
            type="button"
            onClick={this._runFccScraper}
            theme={SR_BUTTON_THEME_ADOBE_YELLOW}
            margin="0 0 20px"
          >
            Run Single FCC Address Scraper
          </SrButton>
          <SrButton
            type="button"
            onClick={this._runFccScraperAll}
            theme={SR_BUTTON_THEME_ADOBE_YELLOW}
            margin="0 0 20px"
          >
            Run Full FCC Address Scraper
          </SrButton>
          <SrButton
            type="button"
            onClick={this._reindexAlgoliaIndex}
            theme={SR_BUTTON_THEME_ADOBE_YELLOW}
            margin="0 0 20px"
          >
            Reset Algolia index
          </SrButton>
          <SrButton
            type="button"
            onClick={this._autoMapAddress}
            theme={SR_BUTTON_THEME_ADOBE_YELLOW}
            margin="0 0 20px"
          >
            Auto-Map Address
          </SrButton>
          <SrButton
            type="button"
            onClick={this._autoMapProvidersByConfigurations}
            theme={SR_BUTTON_THEME_ADOBE_YELLOW}
            margin="0 0 20px"
          >
            Map Providers by City Defaults
          </SrButton>
          <SrButton
            type="button"
            onClick={this._autoMapProvidersByServiceAreas}
            theme={SR_BUTTON_THEME_ADOBE_YELLOW}
            margin="0 0 20px"
          >
            Map Providers by Service Areas
          </SrButton>
          <UPButtonCronJobMissingInternetPlanVariants />

          <SrButton
            type="button"
            onClick={this._toggleAttOrderModal}
            theme={SR_BUTTON_THEME_ADOBE_YELLOW}
            margin="0 0 20px"
          >
            Place ATT Order
          </SrButton>
          {this.state.showAttOrderModal && (
            <UPOrderPlacingForm
              onClose={this._toggleAttOrderModal}
              automatedOrderPlacerType={AUTOMATED_ORDER_PLACER_TYPE_ATT}
            />
          )}
        </SrPartnerSection>

        <SrPartnerSection title="Tenant CSV Import" padding="30px 0 0" titleWeight="700">
          <UPBulkImportTenantForm utilityPartnerList={utilityPartnerList} />
          <SrButton
            type="button"
            onClick={this._toggleImportFileModal}
            theme={SR_BUTTON_THEME_ADOBE_YELLOW}
            margin="0 0 20px"
          >
            Import File
          </SrButton>
          {this.state.showImportFileModal && (
            <UPFileImportAdminModal onClose={this._toggleImportFileModal} />
          )}
        </SrPartnerSection>

        <SrPartnerSection title="Provider Service Areas" padding="30px 0 0" titleWeight="700">
          <UPIngestServiceAreaForm utilityTypes={utilityTypes} />
        </SrPartnerSection>

        <SrPartnerSection title="Partner Sync PM Software" padding="30px 0 0" titleWeight="700">
          <SrDataTableConnected
            itemsPropName="pmsIntegrations"
            rootViewId={DATA_TABLE_SOURCE_UTILITY_PARTNER_PMS_INTEGRATIONS}
            dataSource={DATA_TABLE_SOURCE_UTILITY_PARTNER_PMS_INTEGRATIONS}
            initialViewSettings={{}}
          >
            {({ pmsIntegrations, dataTableApiState }) => {
              return dataTableApiState.inProgress ? null : (
                <UPPartnerSyncPMSoftwareForm
                  utilityPartnerPMSoftwareIntegrationList={pmsIntegrations || []}
                />
              );
            }}
          </SrDataTableConnected>
        </SrPartnerSection>

        <SrPartnerSection title="Listings Scraper" padding="30px 0 0" titleWeight="700">
          <SrButton
            type="button"
            onClick={this._testListingsScraperUrl}
            theme={SR_BUTTON_THEME_ADOBE_YELLOW}
            margin="0 0 20px"
          >
            Test Listings Scraper URL
          </SrButton>
          {scrapedListings && (
            <div>
              <pre>{JSON.stringify(scrapedListings, null, 2)}</pre>
            </div>
          )}
          <SrButton
            type="button"
            onClick={this._testListingsScraperFeed}
            theme={SR_BUTTON_THEME_ADOBE_YELLOW}
          >
            Scrape Partner Listings Feed
          </SrButton>
          {scrapedListingFeed && (
            <div>
              <pre>{JSON.stringify(scrapedListingFeed, null, 2)}</pre>
            </div>
          )}
          <UPDataTableListingScraperLogGroupIndex />
        </SrPartnerSection>

        <SrButton
          type="button"
          onClick={this._testUtilityApi}
          theme={SR_BUTTON_THEME_ADOBE_YELLOW}
          margin="0 0 20px"
        >
          Test Utility Profit API
        </SrButton>
      </SrPartnerPageV2>
    );
  }
}

UtilityProfitAdminPage.propTypes = {
  utilityTypes: PropTypes.array,
  utilityPartnerList: PropTypes.array,
};

UtilityProfitAdminPage.displayName = 'UtilityProfitAdminPage';

export default UtilityProfitAdminPage;
